import React, { useEffect, useRef, useState } from 'react';
import { defaultInputs, firstIncompleteFor, isComplete, WIZARD_STEP_LABELS } from '../data/defaultInputs';
import { calculate } from '../api/calculate';
import { ApiError } from '../api/client';
import { relativeTime } from '../lib/relativeTime';

import StepHeader from '../components/chrome/StepHeader';
import StepFooter from '../components/chrome/StepFooter';

import StepQuality from '../wizard/StepQuality';
import StepSpecifications from '../wizard/StepSpecifications';
import StepConsent from '../wizard/StepConsent';
import StepPartnership from '../wizard/StepPartnership';
import StepEstimate from '../wizard/StepEstimate';
import PhiAttestationGate from '../components/PhiAttestationGate';

const STEP_LABELS = WIZARD_STEP_LABELS;
const NEXT_LABELS = STEP_LABELS.map((l, i) => i + 1 < STEP_LABELS.length ? `Next: ${STEP_LABELS[i + 1]}` : null);
const DEBOUNCE_MS = 300;

function deriveDraftMeta(publicId, savedAt) {
  if (!publicId) return 'Draft · unsaved';
  if (!savedAt) return 'Saved';
  return `Saved · ${relativeTime(savedAt)}`;
}

export default function Wizard({ initialInputs, draftName, publicId, savedAt, onSave, onInputsChange, onOpenPresetPicker, onShowMethodology }) {
  const [inputs, setInputs] = useState(initialInputs || defaultInputs);
  const [step, setStep] = useState(1);
  // maxStepReached tracks which pips in the StepHeader are clickable.
  // For an existing valuation (publicId set), every step's data is
  // already complete, so all five pips should be navigable from the
  // start. For a fresh valuation we follow the natural progression.
  const [maxStepReached, setMaxStepReached] = useState(publicId ? STEP_LABELS.length : 1);
  const [result, setResult] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [calcError, setCalcError] = useState(null);
  const debounceRef = useRef(null);
  const inFlightRef = useRef(0);

  // metaTick is bumped every 30s so the "Saved · 5 min ago" copy updates
  // even when the wizard is otherwise idle. Value itself is unused; the
  // re-render is the point.
  const [, setMetaTick] = useState(0);
  useEffect(() => {
    if (!savedAt) return undefined;
    const id = setInterval(() => setMetaTick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, [savedAt]);

  // Mirror inputs up to App.js so the save dialog has the latest snapshot
  // without subscribing to wizard internals.
  useEffect(() => {
    if (typeof onInputsChange === 'function') onInputsChange(inputs);
  }, [inputs, onInputsChange]);

  // ready gates /api/v1/calculate and the Save buttons; the server rejects
  // null factors / null samples / empty permission so we don't round-trip
  // until inputs would succeed.
  const ready = isComplete(inputs);
  useEffect(() => {
    if (!ready) {
      // Nothing to calculate — clear stale server state so Estimate doesn't
      // keep showing a prior error or result after inputs regress.
      if (result !== null) setResult(null);
      if (calcError !== null) setCalcError(null);
      return undefined;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runCalculate(inputs);
    }, DEBOUNCE_MS);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputs, ready]);

  async function runCalculate(payload) {
    const token = ++inFlightRef.current;
    setCalcLoading(true);
    setCalcError(null);
    try {
      const r = await calculate(payload);
      if (token === inFlightRef.current) setResult(r);
    } catch (err) {
      if (token === inFlightRef.current) {
        setCalcError(err instanceof ApiError ? err.code : 'calc_failed');
      }
    } finally {
      if (token === inFlightRef.current) setCalcLoading(false);
    }
  }

  const change = (field, value) => setInputs((prev) => ({ ...prev, [field]: value }));

  const toggleDataType = (value) =>
    setInputs((prev) => {
      const cur = prev.dataTypes || [];
      return { ...prev, dataTypes: cur.includes(value) ? cur.filter((d) => d !== value) : [...cur, value] };
    });

  const toggleModifier = (value) =>
    setInputs((prev) => {
      const cur = prev.duoModifiers || [];
      return { ...prev, duoModifiers: cur.includes(value) ? cur.filter((m) => m !== value) : [...cur, value] };
    });

  // glowField is the first input that still needs the user's attention
  // on the current step; the step component pipes it to the matching
  // input which renders the amber ring. null when the step is complete.
  const glowField = firstIncompleteFor(step, inputs);
  const stepProps = { inputs, onChange: change, glowField };
  const stepBody =
    step === 1 ? <StepQuality {...stepProps} onOpenPresetPicker={onOpenPresetPicker} /> :
    step === 2 ? <StepSpecifications {...stepProps} onToggleDataType={toggleDataType} /> :
    step === 3 ? <StepConsent {...stepProps} onToggleModifier={toggleModifier} /> :
    step === 4 ? <StepPartnership {...stepProps} /> :
    <StepEstimate
      result={result}
      inputs={inputs}
      name={draftName}
      publicId={publicId}
      ready={ready}
      calcLoading={calcLoading}
      calcError={calcError}
      onRetry={() => runCalculate(inputs)}
      onGoToStep={(n) => setStep(n)}
      onShowMethodology={onShowMethodology}
    />;

  const goNext = () => setStep((s) => {
    const next = Math.min(s + 1, STEP_LABELS.length);
    setMaxStepReached((m) => Math.max(m, next));
    return next;
  });
  const goBack = () => setStep((s) => Math.max(s - 1, 1));

  return (
    <PhiAttestationGate>
    <>
      <StepHeader
        stepLabels={STEP_LABELS}
        currentStep={step}
        draftName={draftName}
        draftMeta={deriveDraftMeta(publicId, savedAt)}
        onSaveExit={onSave && ready ? () => onSave(inputs, result) : undefined}
        maxStepReached={maxStepReached}
        onJumpToStep={(n) => setStep(n)}
      />
      <div style={bodyOuterStyle}>
        <div style={bodyStyle}>
          <div style={mainColStyle}>{stepBody}</div>
        </div>
      </div>
      <StepFooter
        onBack={goBack}
        backDisabled={step === 1}
        onSave={onSave && ready ? () => onSave(inputs, result) : undefined}
        onNext={step < STEP_LABELS.length ? goNext : undefined}
        nextLabel={NEXT_LABELS[step - 1]}
      />
    </>
    </PhiAttestationGate>
  );
}

// bodyOuterStyle handles the full-bleed background; bodyStyle is the
// centered max-width column for wizard steps (no running-value rail).
const bodyOuterStyle = {
  background: 'var(--bg)',
  // Padding-bottom clears the fixed step footer (~72px tall + some breathing).
  paddingBottom: '96px',
};
const bodyStyle = {
  padding: '40px 32px 28px',
  maxWidth: '1200px',
  margin: '0 auto',
};
const mainColStyle = { minWidth: 0 };
